// ============================================================================
// MasseSalarialeReellePage (F-667 / M-681, GC-E) — masse salariale reelle globale
// ============================================================================
// Port du PoC app/(dashboard)/grille-salariale/reel/page.tsx (344 L @ SHA 7e40e4a)
// au standard maison (SPA + useSearchParams). Tous les employes positionnes sur
// la grille reelle de l'annee : taux grille vs reel, ecart et masse au prorata
// exact des changements en cours d'annee (le SERVEUR clippe la periode, M-681).
//
// GARDE M-670 (P-CE14) DOUBLEE CLIENT (comme GC-B/C) : la garde REELLE est le 403
// SERVEUR (requireSalaryGridCE, admin SEUL). Un 403 -> ApiForbiddenError ->
// /error/403 ; le SPA ne DECIDE pas (GA-05), il double la garde serveur.
//
// TRANSPOSITION (§GC-E) : requireAdmin/SSR searchParams -> useSearchParams + hook ;
// StatCard/Segmented/BackLink/PageHeader shadcn -> StatCard local + GsSegmented +
// nav de retour (motif GC-C) + PageHeader @gs/ui ; Intl fr-CA en dur -> helpers
// locale (format.ts) ; formatage UTC des bornes de periode (anti-derive fuseau)
// conserve mais locale-aware. Le CLIENT reproduit les agregats PoC ; le clip de
// periode + la masse annee pleine (fullYearRows) viennent DEJA du serveur (GC-A).
// Regle #4 : zero emoji. Regle #28 : tokens dark-aware.

import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { PageHeader, EmptyState, SkeletonCard, QueryError, GsSegmented } from '@gs/ui';
import { ArrowLeft, Users } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useGrilleCeReel } from '../../hooks/useGrilleCe';
import { realPayrollRowCost, type RealPayrollRow } from '../../lib/salary/types';
import { effectiveBaseSalary } from '../../lib/salary/payroll-by-team';
import {
  buildTeamTargetRows,
  computePayrollRatio,
  DEFAULT_REVENUE_TARGET,
} from '../../lib/salary/payroll-targets';
import { RealPayrollTable } from '../../components/salary/RealPayrollTable';
import { TeamPayrollTargets } from '../../components/salary/TeamPayrollTargets';
import { SalaryPeriodPicker } from '../../components/salary/SalaryPeriodPicker';
import { StatCard, type StatVariance } from '../../components/salary/StatCard';
import { formatCad, formatSignedPct, formatPctPlain } from '../../components/salary/format';

const localeTag = (locale: string): string => (locale.startsWith('en') ? 'en-CA' : 'fr-CA');

// Une date 'YYYY-MM-DD' interpretee en fuseau local pourrait glisser d'un jour :
// on la construit et on la formate en UTC (anti-derive fuseau, VERBATIM PoC).
function isoToUtcDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

// La periode est toujours ⊆ une annee civile => on n'affiche l'annee qu'une fois.
function formatPeriodRange(start: string, end: string, locale: string): string {
  const tag = localeTag(locale);
  const dayMonth = new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const dayMonthYear = new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  return `${dayMonth.format(isoToUtcDate(start))} – ${dayMonthYear.format(isoToUtcDate(end))}`;
}

// Un employe compte dans le cumulatif s'il est actif OU si son dossier touche la
// periode (au moins un jour chiffre OU une estimation grille non nulle) : un
// depart en cours de periode reste dans la masse reelle (VERBATIM PoC).
const countRow = (r: RealPayrollRow): boolean =>
  r.active || r.coverageDays > 0 || (r.unpriced && (r.gridAnnual ?? 0) > 0);

export default function MasseSalarialeReellePage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const yearParam = searchParams.get('year');
  const q = useGrilleCeReel(
    {
      year: yearParam ? Number(yearParam) : null,
      start: searchParams.get('start'),
      end: searchParams.get('end'),
      status: searchParams.get('status'),
    },
    !!user,
  );

  // Garde CLIENT M-670 : 403 serveur (non-admin) -> ecran d'erreur standard.
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  const back = (
    <nav aria-label={t('gest:grilleCe.reel.back')} className="flex items-center">
      <Link
        to="/grille-salariale"
        className="inline-flex touch-manipulation items-center gap-1.5 text-meta font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        {t('gest:grilleCe.reel.back')}
      </Link>
    </nav>
  );

  if (q.isLoading) {
    return (
      <div className="space-y-6">
        {back}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="space-y-6">
        {back}
        <QueryError message={t('gest:grilleCe.reel.loadError')} onRetry={() => void q.refetch()} />
      </div>
    );
  }

  const data = q.data;
  const { year, start, end, isFullYear, status, realRows, fullYearRows, revenueOutlook, targetsConfig } = data;
  // Annees selectionnables : figees ∪ courante — le SERVEUR resout `year` ; le
  // YearPicker de periode reste utile meme sans grille figee (suivi en cours).
  const currentYear = new Date().getFullYear();
  const selectable = [...new Set([year, currentYear])].sort((a, b) => b - a);

  // ── Agregats (VERBATIM PoC) ──────────────────────────────────────────────
  const countedRows = realRows.filter(countRow);
  const total = countedRows.reduce((sum, r) => sum + realPayrollRowCost(r), 0);
  const departedCounted = countedRows.filter((r) => !r.active);
  const departedTotal = departedCounted.reduce((sum, r) => sum + realPayrollRowCost(r), 0);

  const baseTotal = countedRows.reduce((sum, r) => sum + effectiveBaseSalary(r), 0);
  const specialTotal = countedRows.reduce((sum, r) => sum + r.specialHours, 0);
  const premiumsTotal = countedRows.reduce((sum, r) => sum + r.premiums, 0);
  const benefitsTotal = countedRows.reduce((sum, r) => sum + r.benefits, 0);
  const bonusTotal = countedRows.reduce((sum, r) => sum + r.bonus, 0);
  const supplementsTotal = specialTotal + premiumsTotal + benefitsTotal + bonusTotal;
  const avgCost = countedRows.length > 0 ? total / countedRows.length : 0;
  const estimatedRows = countedRows.filter((r) => r.unpriced && r.gridAnnual !== null);
  const estimatedTotal = estimatedRows.reduce((sum, r) => sum + (r.gridAnnual ?? 0), 0);

  // Ratio masse÷revenus : numerateur = masse ANNEE PLEINE (le serveur fournit
  // fullYearRows) ; sur l'annee pleine, fullYearRows === realRows (GC-A).
  const annualPayrollTotal = fullYearRows.filter(countRow).reduce((sum, r) => sum + realPayrollRowCost(r), 0);
  const revenueTargetPct = targetsConfig.revenuePct ?? DEFAULT_REVENUE_TARGET;
  const ratio = computePayrollRatio(annualPayrollTotal, revenueOutlook, revenueTargetPct);
  const ratioAvailable = revenueOutlook.hasData && ratio.ratioProjected !== null;

  const ratioVariances: StatVariance[] | undefined = ratioAvailable
    ? [
        {
          label: t('gest:grilleCe.reel.kpi.ratio.varDelta'),
          value: formatSignedPct(ratio.deltaPts ?? 0, locale),
          tone: (ratio.deltaPts ?? 0) > 0 ? 'negative' : 'positive',
        },
        ...(ratio.ratioBudget !== null
          ? [
              {
                label: t('gest:grilleCe.reel.kpi.ratio.varBudget'),
                value: formatPctPlain(ratio.ratioBudget, locale),
                tone: 'neutral' as const,
              },
            ]
          : []),
        {
          label: t('gest:grilleCe.reel.kpi.ratio.varRevenue'),
          value: formatCad(revenueOutlook.revenueBudgetYear, locale),
          tone: 'neutral',
        },
      ]
    : undefined;
  const ratioHint = !revenueOutlook.hasData
    ? t('gest:grilleCe.reel.kpi.ratio.hintNone', { year })
    : t('gest:grilleCe.reel.kpi.ratio.hint', {
        target: formatPctPlain(revenueTargetPct, locale),
        revenue: formatCad(revenueOutlook.revenueProjected, locale),
        suffix: isFullYear ? '' : t('gest:grilleCe.reel.kpi.ratio.hintFullYearSuffix', { year }),
      });

  const teamTargetRows = buildTeamTargetRows(countedRows, targetsConfig.teamTargets);

  // « Actifs » => actifs + inactifs touchant la periode ; « Tous » => tout le
  // monde. Le total suit toujours countRow, independamment de l'affichage.
  const visibleRows = status === 'all' ? realRows : countedRows;
  const periodLabel = isFullYear ? undefined : formatPeriodRange(start, end, locale);
  const rangeLabel = formatPeriodRange(start, end, locale);

  // Filtre statut : preserve les params retenus (year/start/end) via l'URL.
  const onStatusChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('status', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {back}

      <PageHeader
        title={t('gest:grilleCe.reel.title', { scope: periodLabel ?? String(year) })}
        description={t('gest:grilleCe.reel.description')}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
      />

      {/* Filtres : periode (annee + intervalle) et statut. */}
      <div className="flex flex-col gap-4">
        <SalaryPeriodPicker years={selectable} year={year} start={start} end={end} />
        <GsSegmented
          ariaLabel={t('gest:grilleCe.reel.statusAria')}
          value={status}
          onChange={onStatusChange}
          options={[
            { value: 'active', label: t('gest:grilleCe.reel.status.active') },
            { value: 'all', label: t('gest:grilleCe.reel.status.all') },
          ]}
          className="self-start"
        />
      </div>

      {realRows.length === 0 ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:grilleCe.reel.empty.title')}
          description={t('gest:grilleCe.reel.empty.description')}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard
              tone="primary"
              label={t('gest:grilleCe.reel.kpi.mass.label')}
              value={formatCad(total, locale)}
              hint={t('gest:grilleCe.reel.kpi.mass.hint', { range: rangeLabel, n: countedRows.length })}
              variances={
                departedCounted.length > 0
                  ? [
                      {
                        label: t('gest:grilleCe.reel.kpi.mass.varDeparted', { n: departedCounted.length }),
                        value: formatCad(departedTotal, locale),
                        tone: 'neutral',
                      },
                    ]
                  : undefined
              }
            />
            <StatCard
              label={t('gest:grilleCe.reel.kpi.base.label')}
              value={formatCad(baseTotal, locale)}
              hint={t('gest:grilleCe.reel.kpi.base.hint')}
              variances={
                estimatedRows.length > 0
                  ? [
                      {
                        label: t('gest:grilleCe.reel.kpi.base.varEstimated', { n: estimatedRows.length }),
                        value: formatCad(estimatedTotal, locale),
                        tone: 'neutral',
                      },
                    ]
                  : undefined
              }
            />
            <StatCard
              label={t('gest:grilleCe.reel.kpi.supplements.label')}
              value={formatCad(supplementsTotal, locale)}
              hint={t('gest:grilleCe.reel.kpi.supplements.hint')}
              variances={[
                { label: t('gest:grilleCe.reel.kpi.supplements.varSpecial'), value: formatCad(specialTotal, locale), tone: 'neutral' },
                { label: t('gest:grilleCe.reel.kpi.supplements.varPremiums'), value: formatCad(premiumsTotal, locale), tone: 'neutral' },
                { label: t('gest:grilleCe.reel.kpi.supplements.varBenefits'), value: formatCad(benefitsTotal, locale), tone: 'neutral' },
                { label: t('gest:grilleCe.reel.kpi.supplements.varBonus'), value: formatCad(bonusTotal, locale), tone: 'neutral' },
              ]}
            />
            <StatCard
              label={t('gest:grilleCe.reel.kpi.avg.label')}
              value={formatCad(avgCost, locale)}
              hint={t('gest:grilleCe.reel.kpi.avg.hint', { n: countedRows.length })}
            />
            <StatCard
              label={t('gest:grilleCe.reel.kpi.ratio.label')}
              value={ratioAvailable ? formatPctPlain(ratio.ratioProjected ?? 0, locale) : '—'}
              tone={
                !ratioAvailable
                  ? 'neutral'
                  : (ratio.ratioProjected ?? 0) > revenueTargetPct
                    ? 'primary'
                    : 'success'
              }
              hint={ratioHint}
              variances={ratioVariances}
            />
          </div>

          <TeamPayrollTargets rows={teamTargetRows} year={year} periodLabel={periodLabel} />

          <RealPayrollTable rows={visibleRows} year={year} countRow={countRow} periodLabel={periodLabel} />
        </>
      )}
    </div>
  );
}
